// src/Maintenance.js
import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { clearNotificationSubscription } from './utils/notificationUtils';
import { apiFetch } from './utils/apiUtils';
import {
  Container,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  Paper,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  LinearProgress,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';

const Maintenance = ({ type = "excel" }) => {
  // Excel Data Loading state
  const [file, setFile] = useState(null);
  const [sheetName, setSheetName] = useState("Sheet1");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  
  // Notification Management state
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationError, setNotificationError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  // User Management state
  const [userMessage, setUserMessage] = useState("");
  const [userError, setUserError] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Handle clearing notification subscription
  const handleClearNotifications = (mode) => {
    setIsProcessing(true);
    setNotificationMessage("");
    setNotificationError("");
    
    let clearPromise;
    
    if (mode === 'specific' && selectedUser) {
      // Clear notifications for a specific user
      clearPromise = clearNotificationSubscription(selectedUser);
    } else if (mode === 'all') {
      // Clear notifications for all users
      clearPromise = clearNotificationSubscription('', true);
    } else {
      // Default to current user
      clearPromise = clearNotificationSubscription();
    }
    
    clearPromise
      .then((result) => {
        if (mode === 'specific') {
          setNotificationMessage(`Notification subscriptions cleared successfully for user: ${selectedUser}`);
        } else if (mode === 'all') {
          setNotificationMessage(`Notification subscriptions cleared for all users. ${result.count || 0} subscriptions removed.`);
        } else {
          setNotificationMessage("Your notification subscription cleared successfully. You will be prompted for permissions again when needed.");
        }
      })
      .catch(err => {
        setNotificationError(`Error clearing notification subscription: ${err.message}`);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };
  // SQL Query Tool state
  const [sqlQuery, setSqlQuery] = useState("");
  const [queryResults, setQueryResults] = useState(null);
  const [queryError, setQueryError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  // Password digits state (8 separate fields)
  const [passwordDigits, setPasswordDigits] = useState(Array(8).fill(""));

  // Check if query requires a password (UPDATE or DELETE)
  const checkPasswordRequired = (query) => {
    if (!query) return false;
    const trimmedQuery = query.trim().toLowerCase();
    return trimmedQuery.startsWith('update') || trimmedQuery.startsWith('delete');
  };

  // Update SQL query and check if password is required
  const handleSqlQueryChange = (e) => {
    const newQuery = e.target.value;
    setSqlQuery(newQuery);
    setPasswordRequired(checkPasswordRequired(newQuery));
  };

  // Handle file input change.
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Parse Excel and send data to server.
  const handleLoadExcel = () => {
    setError("");
    setMessage("");
    if (!file) {
      setError("Please select an Excel file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      try {
        const workbook = XLSX.read(bstr, { type: "binary" });
        // Use the specified sheet or the first sheet if not found.
        const sheet = workbook.Sheets[sheetName] || workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        // Post the data to the server endpoint.
        fetch(`${process.env.REACT_APP_API_URL}/seed-source-data`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({ data }),
        })
          .then((res) => {
            if (!res.ok) throw new Error("Seeding failed");
            return res.json();
          })
          .then((result) => {
            setMessage(result.message || "Data seeded successfully.");
          })
          .catch((err) => setError(err.message));
      } catch (err) {
        setError("Error reading file: " + err.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Handle password digit change
  const handlePasswordDigitChange = (index, value) => {
    // Allow both 1-digit and 2-digit numbers (0-99)
    if (!/^\d{0,2}$/.test(value)) {
      return;
    }
    
    const newPasswordDigits = [...passwordDigits];
    newPasswordDigits[index] = value;
    setPasswordDigits(newPasswordDigits);
  };

  // Get combined password and validate
  const getPasswordAndValidate = () => {
    // Check if all fields are filled
    if (passwordDigits.some(digit => digit === "")) {
      return { isValid: false, errorMessage: "Please fill all 8 password fields" };
    }

    // Combine all fields into a single password string
    const password = passwordDigits.join("");
    
    // Extract individual digits (same as server-side validation)
    const digits = password.match(/\d/g) || [];
    
    // Calculate sum of individual digits
    const sum = digits.reduce((acc, digit) => acc + parseInt(digit, 10), 0);
    
    // Check if we have at least 8 digits
    if (digits.length < 8) {
      return { isValid: false, errorMessage: `Need at least 8 digits, but found ${digits.length}` };
    }
    
    // Check if sum is 50
    if (sum !== 50) {
      return { isValid: false, errorMessage: `Sum of all digits is ${sum}, but should be 50` };
    }
    
    return { isValid: true, password };
  };

  // Execute SQL query
  const handleExecuteQuery = () => {
    setQueryError("");
    setQueryResults(null);
    setIsLoading(true);
    
    if (!sqlQuery.trim()) {
      setQueryError("Please enter a SQL query");
      setIsLoading(false);
      return;
    }

    // Check if password is required
    if (passwordRequired) {
      const { isValid, errorMessage, password } = getPasswordAndValidate();
      
      if (!isValid) {
        setQueryError(errorMessage);
        setIsLoading(false);
        return;
      }
      
      // Prepare the request body with password
      const requestBody = {
        query: sqlQuery,
        password
      };
      
      apiFetch(`/api/admin/execute-query`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      })
        .then((res) => res.json())
        .then((data) => {
          setIsLoading(false);
          if (data.error) {
            setQueryError(data.error);
            return;
          }
          setQueryResults(data);
        })
        .catch((err) => {
          setIsLoading(false);
          setQueryError("Error executing query: " + err.message);
        });
    } else {
      // No password required, just send the query
      const requestBody = {
        query: sqlQuery
      };
      
      apiFetch(`/api/admin/execute-query`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      })
        .then((res) => res.json())
        .then((data) => {
          setIsLoading(false);
          if (data.error) {
            setQueryError(data.error);
            return;
          }
          setQueryResults(data);
        })
        .catch((err) => {
          setIsLoading(false);
          setQueryError("Error executing query: " + err.message);
        });
    }

  };

  // Open confirmation dialog for deleting users
  const openConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  // Close confirmation dialog
  const closeConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  // Delete all users from the database
  const handleDeleteAllUsers = () => {
    setIsDeleting(true);
    setUserMessage("");
    setUserError("");
    
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/users/delete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({ deleteAll: true }),
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`Failed to delete users: ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        setUserMessage(data.message || "All users have been successfully deleted");
        setConfirmDialogOpen(false);
      })
      .catch(err => {
        setUserError(`Error: ${err.message}`);
        setConfirmDialogOpen(false);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  // Copy results to clipboard
  const handleCopyResults = () => {
    if (!queryResults || !queryResults.rows) {
      return;
    }

    // Convert rows to JSON string for clipboard
    const resultsText = JSON.stringify(queryResults.rows, null, 2);
    navigator.clipboard.writeText(resultsText)
      .then(() => {
        alert("Results copied to clipboard");
      })
      .catch(err => {
        console.error("Failed to copy: ", err);
      });
  };

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        {type === "excel" ? "Data Load Maintenance" : 
         type === "sql" ? "SQL Query Tool" :
         type === "notifications" ? "Notification Management" :
         "User Management"}
      </Typography>
      
      {/* Excel Data Loading Section - Only shown when type is "excel" */}
      {type === "excel" && (
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Load Excel Data to DB</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          <Box sx={{ mb: 2 }}>
            <input type="file" accept=".xlsx, .xls, .xlsm" onChange={handleFileChange} />
          </Box>
          <TextField
            label="Sheet Name (default: Sheet1)"
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
          />
          <Button variant="contained" color="primary" onClick={handleLoadExcel}>
            Load Excel Data to DB
          </Button>
        </Paper>
      )}
      
      {/* User Management Section - Only shown when type is "users" */}
      {type === "users" && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>User Management</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Use this tool to manage user accounts. Deleting users will allow you to recreate them with proper email addresses.
          </Typography>
          
          {userError && <Alert severity="error" sx={{ mb: 2 }}>{userError}</Alert>}
          {userMessage && <Alert severity="success" sx={{ mb: 2 }}>{userMessage}</Alert>}
          
          <Box sx={{ mt: 3, mb: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                Warning: This is a destructive operation
              </Typography>
              <Typography variant="body2">
                Deleting users will permanently remove all user accounts from the database. This action cannot be undone.
                Users will need to re-register to regain access to the system.
              </Typography>
            </Alert>
            
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={openConfirmDialog}
              disabled={isDeleting}
              sx={{ mt: 2 }}
            >
              Delete All Users
            </Button>
            
            {isDeleting && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
          
          {/* Confirmation Dialog */}
          <Dialog
            open={confirmDialogOpen}
            onClose={closeConfirmDialog}
          >
            <DialogTitle>Confirm User Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you absolutely sure you want to delete all users from the database?
                This action cannot be undone and will require users to re-register.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeConfirmDialog} color="primary">
                Cancel
              </Button>
              <Button onClick={handleDeleteAllUsers} color="error" variant="contained">
                Yes, Delete All Users
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      )}

      {/* SQL Query Tool Section - Only shown when type is "sql" */}
      {type === "sql" && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>SQL Query Tool</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Enter SQL SELECT queries to execute against the database. For security reasons, only SELECT statements are allowed.
          </Typography>
          
          {queryError && <Alert severity="error" sx={{ mb: 2 }}>{queryError}</Alert>}
          
          <TextField
            label="SQL Query"
            multiline
            rows={12}
            value={sqlQuery}
            onChange={handleSqlQueryChange}
            variant="outlined"
            fullWidth
            sx={{ mb: 2, minHeight: '300px' }}
            placeholder="SELECT * FROM table_name LIMIT 10"
          />
          
          {passwordRequired && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Password Required (8 numbers that sum to 50)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This query requires a password. Enter 8 numbers (1 or 2 digits each) where the individual digits sum to exactly 50.
                (For example: 5,6,7,8,9,1,5,9 or 10,4,22,5,3,2,1,3)
              </Typography>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexWrap: 'wrap',
                  gap: 1,
                  mb: 1
                }}
              >
                {passwordDigits.map((digit, index) => (
                  <TextField
                    key={index}
                    label={`#${index + 1}`}
                    value={digit}
                    onChange={(e) => handlePasswordDigitChange(index, e.target.value)}
                    inputProps={{ 
                      maxLength: 2,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      style: { textAlign: 'center' }
                    }}
                    variant="outlined"
                    sx={{ 
                      width: 'calc(12.5% - 8px)',
                      minWidth: '60px' 
                    }}
                  />
                ))}
              </Box>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                Current sum of digits: {(passwordDigits.join("").match(/\d/g) || []).reduce((acc, digit) => acc + parseInt(digit, 10), 0)} / 50
              </Typography>
            </Box>
          )}
        
          <Button
            variant="contained" 
            color="primary" 
            onClick={handleExecuteQuery}
            disabled={isLoading}
            sx={{ mb: 2 }}
          >
            Execute Query
          </Button>
          
          {isLoading && <LinearProgress sx={{ mb: 2 }} />}
          
          {queryResults && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">
                  Results: {queryResults.rowCount} rows returned
                </Typography>
                <IconButton onClick={handleCopyResults} title="Copy results to clipboard">
                  <ContentCopyIcon />
                </IconButton>
              </Box>
            
              {queryResults.rowCount > 0 ? (
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        {queryResults.columns.map((column, index) => (
                          <TableCell key={index}>{column}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {queryResults.rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                          {queryResults.columns.map((column, colIndex) => (
                            <TableCell key={`${rowIndex}-${colIndex}`}>
                              {row[column] !== null && row[column] !== undefined 
                                ? String(row[column]) 
                                : <span style={{ color: 'gray' }}>null</span>}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">The query executed successfully but returned no results.</Alert>
              )}
            </Box>
          )}
        </Paper>
      )}
      
      {/* Notification Management Section - Only shown when type is "notifications" */}
      {type === "notifications" && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Notification Management</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Use this tool to reset notification permissions and subscriptions for users. This is useful when users are
            experiencing issues with notifications or have changed devices.
          </Typography>
          
          {notificationError && <Alert severity="error" sx={{ mb: 2 }}>{notificationError}</Alert>}
          {notificationMessage && <Alert severity="success" sx={{ mb: 2 }}>{notificationMessage}</Alert>}
          
          <Box sx={{ mt: 3, mb: 3 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                What this does
              </Typography>
              <Typography variant="body2">
                Clearing notification subscriptions will:
                <ul>
                  <li>Remove device registrations from the server</li>
                  <li>Unregister from push notifications</li>
                  <li>Reset notification permissions in browsers</li>
                  <li>Allow users to be prompted for notification permissions again</li>
                </ul>
                This can help resolve notification issues after system updates or when users change their devices or browsers.
              </Typography>
            </Alert>
            
            <Box sx={{ mt: 3, mb: 3, border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Reset Notifications For:
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  label="Username"
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ mr: 2 }}
                  placeholder="Enter a specific username"
                  value={selectedUser}
                  disabled={isProcessing}
                  onChange={(e) => setSelectedUser(e.target.value)}
                />
                
                <Button
                  variant="contained" 
                  color="primary"
                  startIcon={<NotificationsOffIcon />}
                  onClick={() => handleClearNotifications('specific')}
                  disabled={isProcessing || !selectedUser}
                >
                  Reset For User
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                  variant="outlined"
                  color="primary" 
                  startIcon={<NotificationsOffIcon />}
                  onClick={() => handleClearNotifications('current')}
                  disabled={isProcessing}
                >
                  Reset My Notifications
                </Button>
                
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<NotificationsOffIcon />}
                  onClick={() => handleClearNotifications('all')}
                  disabled={isProcessing}
                >
                  Reset All Users
                </Button>
              </Box>
            </Box>
            
            {isProcessing && <LinearProgress sx={{ mt: 2 }} />}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default Maintenance;